import { useState, useRef, useEffect, useMemo } from "preact/hooks"
import { Button } from "../../../components/ui/button"
import { Input } from "../../../components/ui/input"
import { Label } from "../../../components/ui/label"
import { Badge } from "../../../components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog"
import { Plus, Save, Star, Copy, Trash2, Edit3, Lock, Unlock, Archive, ArchiveRestore } from "lucide-react"
import { ForecastVersion, VersionManager, SalesHierarchyNode, TimePeriod } from "../types"
import { UserState } from "@/auth/user"
import { can_manage_forecast_versions } from "../utils/version_access"
import { useOrgSettings } from "@/org_settings/state"
import {
  build_version_forest,
  list_title_for_version_card,
  parse_restore_description_to_table,
  type VersionTreeNode,
} from "../utils/version_display"

/** `plan_kind`: a forecast and a budget are separate plans, each with its own primary. */
const PLAN_KIND_OPTIONS: { value: string; label: string }[] = [
  { value: "forecast", label: "Forecast" },
  { value: "budget", label: "Budget" },
]

/** "Primary forecast" / "Primary budget": the star label for a version of its plan kind. */
export function primary_label_for_version(version: Pick<ForecastVersion, "plan_kind">): string {
  return (version.plan_kind ?? "forecast") === "budget" ? "Primary budget" : "Primary forecast"
}

/** Labeled choices for `scenario_kind` — the backend column and badge already exist. */
const SCENARIO_KIND_OPTIONS: { value: string; label: string }[] = [
  { value: "operating_plan", label: "Operating plan" },
  { value: "growth_story", label: "Growth story" },
  { value: "board_case", label: "Board case" },
]

/**
 * Years a new blank plan can cover: last year through two years out. From October the next year
 * is preselected — that is planning season, and a blank plan made then is next year's budget.
 */
export function new_version_year_options(now: Date): number[] {
  const y = now.getFullYear()
  return [y - 1, y, y + 1, y + 2]
}

export function default_new_version_year(now: Date): number {
  const y = now.getFullYear()
  return now.getMonth() >= 9 ? y + 1 : y
}

function truncate_for_card(text: string, maxChars: number): string {
  const t = text.trim()
  if (t.length <= maxChars) {
    return t
  }
  return `${t.slice(0, maxChars)}…`
}

interface VersionCardBlockProps {
  version: ForecastVersion
  is_active: boolean
  parent_label: string | null
  on_select: () => void
  on_edit: () => void
  on_duplicate: () => void
  on_set_primary: () => void
  on_toggle_locked: () => void
  on_toggle_archived: () => void
  on_delete: () => void
  formatDate: (dateString: string) => string
}

function VersionDescriptionBlock(props: { description: string | undefined }) {
  const rows = parse_restore_description_to_table(props.description)
  if (rows && rows.length > 0) {
    return (
      <div className="mt-2 overflow-x-auto rounded-md border border-gray-100 bg-white">
        <table className="w-full min-w-[12rem] border-collapse text-left text-[11px]">
          <tbody>
            {rows.map((row) => (
              <tr key={row.label} className="border-b border-gray-50 last:border-b-0">
                <th className="w-[38%] align-top py-1.5 pl-2 pr-2 font-medium text-gray-500">{row.label}</th>
                <td className="align-top py-1.5 pr-2 text-gray-800 whitespace-pre-wrap break-words">{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }
  if (props.description?.trim()) {
    return (
      <p className="mt-2 text-xs leading-relaxed text-gray-600 line-clamp-2 break-words" title={props.description}>
        {props.description}
      </p>
    )
  }
  return null
}

function VersionCardBlock(props: VersionCardBlockProps) {
  // Choosing the primary plan, locking and deleting are admin acts; the server refuses them too.
  const can_manage = can_manage_forecast_versions(UserState.use(), useOrgSettings().forecast_approvals_required)
  const { version, is_active, parent_label } = props
  const title = list_title_for_version_card(version.name)
  return (
    <div
      className={`rounded-lg border bg-white transition-colors ${
        is_active
          ? "border-blue-400 bg-blue-50/60 ring-1 ring-blue-200"
          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50/80"
      }`}
    >
      <button
        type="button"
        className="w-full min-w-0 cursor-pointer p-3 text-left"
        onClick={props.on_select}
      >
        {parent_label ? (
          <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-400">Branched from · {parent_label}</p>
        ) : null}
        <p className="text-sm font-medium leading-snug text-gray-900 break-words" title={version.name}>
          {title}
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {version.scenario_kind ? (
            <Badge variant="secondary" className="text-[10px] font-normal capitalize">
              {version.scenario_kind.replaceAll("_", " ")}
            </Badge>
          ) : null}
          {(version.plan_kind ?? "forecast") === "budget" ? (
            <Badge variant="secondary" className="bg-indigo-100 text-indigo-800 text-[10px] font-medium">
              Budget
            </Badge>
          ) : null}
          {version.is_primary ? (
            <Badge variant="default" className="bg-amber-500 text-white text-[10px] font-medium">
              <Star className="h-3 w-3 mr-0.5" aria-hidden />
              {primary_label_for_version(version)}
            </Badge>
          ) : null}
          {version.locked_at ? (
            <Badge variant="secondary" className="bg-gray-200 text-gray-700 text-[10px] font-medium">
              <Lock className="h-3 w-3 mr-0.5" aria-hidden />
              Locked
            </Badge>
          ) : null}
          {version.archived_at ? (
            <Badge variant="secondary" className="bg-gray-100 text-gray-500 text-[10px] font-medium">
              <Archive className="h-3 w-3 mr-0.5" aria-hidden />
              Archived
            </Badge>
          ) : null}
        </div>
        <VersionDescriptionBlock description={version.description} />
        <p className="mt-2 text-[11px] text-gray-500">Updated {props.formatDate(version.updated_at)}</p>
      </button>
      <div className="flex flex-wrap items-center justify-end gap-1 border-t border-gray-100 bg-gray-50/50 px-2 py-2">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0 text-gray-700"
          title="Rename / edit details"
          aria-label={`Edit ${version.name}`}
          onClick={(e) => {
            e.stopPropagation()
            props.on_edit()
          }}
        >
          <Edit3 className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0 text-gray-700"
          title="Duplicate this version"
          aria-label={`Duplicate ${version.name}`}
          onClick={(e) => {
            e.stopPropagation()
            props.on_duplicate()
          }}
        >
          <Copy className="h-4 w-4" />
        </Button>
        {!version.is_primary && can_manage ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0 text-gray-700"
            title={`Set as ${primary_label_for_version(version).toLowerCase()}`}
            aria-label={`Set ${version.name} as ${primary_label_for_version(version).toLowerCase()}`}
            onClick={(e) => {
              e.stopPropagation()
              props.on_set_primary()
            }}
          >
            <Star className="h-4 w-4" />
          </Button>
        ) : null}
        {can_manage ? (
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0 text-gray-700"
          title={version.locked_at ? "Unlock version (allow edits)" : "Lock version (block all edits)"}
          aria-label={version.locked_at ? `Unlock ${version.name}` : `Lock ${version.name}`}
          onClick={(e) => {
            e.stopPropagation()
            props.on_toggle_locked()
          }}
        >
          {version.locked_at ? <Unlock className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
        </Button>
        ) : null}
        {!version.is_primary ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0 text-gray-700"
            title={version.archived_at ? "Restore from archive" : "Archive (hide from this list)"}
            aria-label={version.archived_at ? `Unarchive ${version.name}` : `Archive ${version.name}`}
            onClick={(e) => {
              e.stopPropagation()
              props.on_toggle_archived()
            }}
          >
            {version.archived_at ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
          </Button>
        ) : null}
        {/* The primary plan and a locked version stay: make another version primary, or unlock, first (the server refuses too). */}
        {!version.is_primary && !version.locked_at && can_manage ? (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0 text-red-600 hover:bg-red-50 hover:text-red-700"
            title="Delete version"
            aria-label={`Delete ${version.name}`}
            onClick={(e) => {
              e.stopPropagation()
              props.on_delete()
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ) : null}
      </div>
    </div>
  )
}

interface VersionTreeBranchProps {
  node: VersionTreeNode
  depth: number
  parent_name: string | null
  versionManager: VersionManager
  onVersionChange: (versionId: string) => void
  onClose: () => void
  onVersionDelete: (version: ForecastVersion) => void
  onVersionDuplicate: (versionId: string, name: string) => void
  onVersionSetPrimary: (versionId: string) => void
  onVersionToggleLocked: (version: ForecastVersion) => void
  onVersionToggleArchived: (version: ForecastVersion) => void
  handleEditVersion: (version: ForecastVersion) => void
  handleDuplicateVersion: (version: ForecastVersion) => void
  formatDate: (dateString: string) => string
}

function VersionTreeBranch(props: VersionTreeBranchProps) {
  const v = props.node.version
  const is_active = v.id === props.versionManager.current_version_id
  const wrap = props.depth > 0 ? "ml-2 border-l-2 border-gray-100 pl-3 space-y-2" : "space-y-2"

  return (
    <div className={wrap}>
      <VersionCardBlock
        version={v}
        is_active={is_active}
        parent_label={props.parent_name}
        formatDate={props.formatDate}
        on_select={() => {
          props.onVersionChange(v.id)
          props.onClose()
        }}
        on_edit={() => props.handleEditVersion(v)}
        on_duplicate={() => props.handleDuplicateVersion(v)}
        on_set_primary={() => props.onVersionSetPrimary(v.id)}
        on_toggle_locked={() => props.onVersionToggleLocked(v)}
        on_toggle_archived={() => props.onVersionToggleArchived(v)}
        on_delete={() => props.onVersionDelete(v)}
      />
      {props.node.children.map((ch: VersionTreeNode) => (
        <VersionTreeBranch
          key={ch.version.id}
          node={ch}
          depth={props.depth + 1}
          parent_name={v.name}
          versionManager={props.versionManager}
          onVersionChange={props.onVersionChange}
          onClose={props.onClose}
          onVersionDelete={props.onVersionDelete}
          onVersionDuplicate={props.onVersionDuplicate}
          onVersionSetPrimary={props.onVersionSetPrimary}
          onVersionToggleLocked={props.onVersionToggleLocked}
          onVersionToggleArchived={props.onVersionToggleArchived}
          handleEditVersion={props.handleEditVersion}
          handleDuplicateVersion={props.handleDuplicateVersion}
          formatDate={props.formatDate}
        />
      ))}
    </div>
  )
}

interface VersionManagerProps {
  versionManager: VersionManager
  currentData: SalesHierarchyNode[]
  currentPeriods: TimePeriod[]
  compareMode: boolean
  comparisonPeriods: TimePeriod[]
  onVersionChange: (versionId: string) => void
  /** `fiscal_year` is the calendar year the blank plan's twelve columns cover. */
  onCreateBlankVersion: (name: string, description?: string, scenario_kind?: string, fiscal_year?: number, plan_kind?: string) => void
  onVersionUpdate: (versionId: string, name: string, description?: string, scenario_kind?: string) => void
  onVersionDelete: (versionId: string) => void
  onVersionDuplicate: (versionId: string, name: string) => void
  onVersionSetPrimary: (versionId: string) => void
  onVersionSetLocked: (versionId: string, locked: boolean) => void
  onVersionSetArchived: (versionId: string, archived: boolean) => void
  /** Fork the current in-memory grid (incl. unsaved edits) into a new child version. */
  onVersionForkCurrent: (name: string, description?: string, scenario_kind?: string, plan_kind?: string) => void
  onVersionSave: (versionId: string) => void
  /** Start comparing the open version against another, rendered on the grid itself. */
  onVersionCompareStart: (version: ForecastVersion) => void
  activeCompareName?: string | null
  onVersionCompareStop: () => void
  isOpen: boolean
  onClose: () => void
}

export function VersionManagerComponent({
  versionManager,
  currentData,
  currentPeriods,
  compareMode,
  comparisonPeriods,
  onVersionChange,
  onCreateBlankVersion,
  onVersionUpdate,
  onVersionDelete,
  onVersionDuplicate,
  onVersionSetPrimary,
  onVersionSetLocked,
  onVersionSetArchived,
  onVersionForkCurrent,
  onVersionSave,
  onVersionCompareStart,
  activeCompareName,
  onVersionCompareStop,
  isOpen,
  onClose
}: VersionManagerProps) {
  const [isCreating, setIsCreating] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editingVersion, setEditingVersion] = useState<ForecastVersion | null>(null)
  const [newVersionName, setNewVersionName] = useState("")
  const [newVersionDescription, setNewVersionDescription] = useState("")
  const [newVersionScenarioKind, setNewVersionScenarioKind] = useState("operating_plan")
  const [newVersionPlanKind, setNewVersionPlanKind] = useState("forecast")
  const [createMode, setCreateMode] = useState<"blank" | "duplicate" | "fork">("blank")
  const [newVersionYear, setNewVersionYear] = useState<number>(default_new_version_year(new Date()))
  const [duplicateSourceId, setDuplicateSourceId] = useState<string>("")
  const [pendingDeleteVersion, setPendingDeleteVersion] = useState<ForecastVersion | null>(null)
  const [showArchived, setShowArchived] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const viewer_can_manage = can_manage_forecast_versions(UserState.use(), useOrgSettings().forecast_approvals_required)

  const currentVersion = versionManager.versions.find(v => v.id === versionManager.current_version_id)

  const archived_count = useMemo(
    () => versionManager.versions.filter((v: ForecastVersion) => v.archived_at).length,
    [versionManager.versions]
  )

  const version_forest = useMemo(() => {
    // Archived versions collapse out of the list (the open one always stays visible).
    const visible = showArchived
      ? versionManager.versions
      : versionManager.versions.filter(
          (v: ForecastVersion) => !v.archived_at || v.id === versionManager.current_version_id
        )
    return build_version_forest(visible)
  }, [versionManager.versions, versionManager.current_version_id, showArchived])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement
      // Radix portals (the diff dialog, the delete confirm, Select dropdowns) render outside
      // the panel div — clicking inside them must not close (and unmount) everything. A Select in
      // its default item-aligned position has no popper wrapper, only a `listbox` — picking a
      // scenario or a "copy from" version used to close the panel and lose the form.
      if (
        target.closest?.(
          '[role="dialog"], [role="alertdialog"], [role="listbox"], [role="option"], [data-radix-popper-content-wrapper], [data-radix-select-viewport]'
        )
      ) {
        return
      }
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose()
        setIsCreating(false)
        setIsEditing(false)
        setNewVersionName("")
        setNewVersionDescription("")
        setEditingVersion(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [onClose])

  // The panel keeps its state while closed (it only stops rendering), so an edit form left open
  // was still open — and un-escapable — the next time the panel opened. Every open starts clean.
  useEffect(() => {
    if (!isOpen) return
    setIsCreating(false)
    setIsEditing(false)
    setEditingVersion(null)
    setNewVersionName("")
    setNewVersionDescription("")
    setNewVersionScenarioKind("operating_plan")
    setCreateMode("blank")
    setNewVersionYear(default_new_version_year(new Date()))
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    function handleEscape(event: KeyboardEvent) {
      if (event.key !== "Escape") return
      if (isCreating || isEditing) {
        handleCancel()
      } else {
        onClose()
      }
    }
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [isOpen, isCreating, isEditing, onClose])

  function handleCreateVersion() {
    if (!newVersionName.trim()) {
      return
    }
    const name = newVersionName.trim()
    const description = newVersionDescription.trim() || undefined
    if (createMode === "duplicate") {
      const sourceId = duplicateSourceId || versionManager.current_version_id || ""
      if (!sourceId) {
        return
      }
      onVersionDuplicate(sourceId, name)
    } else if (createMode === "fork") {
      onVersionForkCurrent(name, description, newVersionScenarioKind, newVersionPlanKind)
    } else {
      onCreateBlankVersion(name, description, newVersionScenarioKind, newVersionYear, newVersionPlanKind)
    }
    setNewVersionName("")
    setNewVersionDescription("")
    setNewVersionScenarioKind("operating_plan")
    setNewVersionPlanKind("forecast")
    setIsCreating(false)
    setCreateMode("blank")
  }

  function handleEditVersion(version: ForecastVersion) {
    setEditingVersion(version)
    setNewVersionName(version.name)
    setNewVersionDescription(version.description || "")
    setNewVersionScenarioKind(version.scenario_kind || "operating_plan")
    setIsEditing(true)
    setIsCreating(false)
  }

  function handleUpdateVersion() {
    if (editingVersion && newVersionName.trim()) {
      onVersionUpdate(
        editingVersion.id,
        newVersionName.trim(),
        newVersionDescription.trim() || undefined,
        newVersionScenarioKind
      )
      setNewVersionName("")
      setNewVersionDescription("")
      setNewVersionScenarioKind("operating_plan")
      setEditingVersion(null)
      setIsEditing(false)
    }
  }

  function handleCancel() {
    setNewVersionName("")
    setNewVersionDescription("")
    setNewVersionScenarioKind("operating_plan")
    setEditingVersion(null)
    setIsCreating(false)
    setIsEditing(false)
  }

  function handleToggleLocked(version: ForecastVersion) {
    onVersionSetLocked(version.id, !version.locked_at)
  }

  function handleDuplicateVersion(version: ForecastVersion) {
    const newName = `${version.name} (Copy)`
    onVersionDuplicate(version.id, newName)
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50" ref={dropdownRef}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/10" onClick={onClose} />
      
      {/* Panel: wide enough for names + actions; scroll inside on short viewports */}
      <div
        className="absolute top-16 right-3 sm:right-4 w-[min(26rem,calc(100vw-1.5rem))] max-h-[min(40rem,calc(100vh-4rem))] flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
          <div className="flex-1 overflow-y-auto overscroll-contain p-4 space-y-4">
            {/* Current Version */}
            <div className="rounded-lg border border-gray-100 bg-gray-50/80 p-3 space-y-3">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Current version</p>
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-start gap-2">
                  <h3 className="min-w-0 flex-1 text-sm font-semibold leading-snug text-gray-900 break-words">
                    {currentVersion ? currentVersion.name : "Untitled Version"}
                  </h3>
                  {currentVersion?.is_primary ? (
                    <Badge variant="default" className="shrink-0 bg-amber-500 text-white text-[10px] font-medium">
                      <Star className="h-3 w-3 mr-0.5" aria-hidden />
                      {primary_label_for_version(currentVersion)}
                    </Badge>
                  ) : null}
                </div>
                {currentVersion?.description ? (
                  <p
                    className="text-xs leading-relaxed text-gray-600 break-words"
                    title={currentVersion.description}
                  >
                    {truncate_for_card(currentVersion.description, 180)}
                  </p>
                ) : null}
              </div>
              {currentVersion?.locked_at ? (
                <p className="flex items-center gap-1.5 text-xs text-gray-500">
                  <Lock className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {viewer_can_manage
                    ? "This version is locked — unlock it below to make changes."
                    : "This version is locked — an org admin can unlock it."}
                </p>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  onVersionSave(versionManager.current_version_id || "")
                  onClose()
                }}
                disabled={!versionManager.current_version_id || Boolean(currentVersion?.locked_at)}
                className="w-full"
              >
                <Save className="h-4 w-4 mr-2 shrink-0" />
                Save changes
              </Button>
              {/* Inline compare picker: choosing a version renders the comparison on the grid. */}
              {activeCompareName ? (
                <div className="flex items-center justify-between gap-2 rounded-md border border-indigo-200 bg-indigo-50/70 px-2.5 py-1.5">
                  <span className="min-w-0 truncate text-xs text-indigo-900">
                    Comparing vs <strong>{activeCompareName}</strong>
                  </span>
                  <button
                    type="button"
                    className="shrink-0 text-xs font-medium text-indigo-700 hover:text-indigo-900"
                    onClick={onVersionCompareStop}
                  >
                    Exit
                  </button>
                </div>
              ) : (
                <Select
                  value={undefined}
                  onValueChange={(id: string) => {
                    const version = versionManager.versions.find((v: ForecastVersion) => v.id === id)
                    if (version) {
                      onVersionCompareStart(version)
                      onClose()
                    }
                  }}
                >
                  <SelectTrigger className="h-9 w-full" disabled={versionManager.versions.length < 2}>
                    <SelectValue placeholder="Compare to…" />
                  </SelectTrigger>
                  <SelectContent>
                    {versionManager.versions
                      .filter((v: ForecastVersion) => v.id !== versionManager.current_version_id)
                      .map((v: ForecastVersion) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Create New Version */}
            {isCreating ? (
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">Create New Version</h4>
                <div className="space-y-2">
                  <Label>Start from</Label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={createMode === "blank" ? "default" : "outline"}
                      onClick={() => setCreateMode("blank")}
                    >
                      Blank
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={createMode === "duplicate" ? "default" : "outline"}
                      onClick={() => setCreateMode("duplicate")}
                    >
                      Copy existing…
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={createMode === "fork" ? "default" : "outline"}
                      disabled={!versionManager.current_version_id}
                      title="Fork the grid exactly as it is right now — including edits you have not saved"
                      onClick={() => setCreateMode("fork")}
                    >
                      Current grid
                    </Button>
                  </div>
                  {createMode === "fork" ? (
                    <p className="text-[11px] leading-snug text-gray-500">
                      Saves the grid as you see it — including unsaved edits — into a new scenario
                      branched from {currentVersion?.name ?? "the current version"}, and switches to it.
                    </p>
                  ) : null}
                </div>
                {createMode === "blank" ? (
                  <div>
                    <Label htmlFor="new-version-year">Plan year</Label>
                    {/* A blank plan used to be pinned to the current year, so a 2027 budget had no 2027 months. */}
                    <Select value={String(newVersionYear)} onValueChange={(v: string) => setNewVersionYear(Number(v))}>
                      <SelectTrigger id="new-version-year" className="mt-1 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {new_version_year_options(new Date()).map((year: number) => (
                          <SelectItem key={year} value={String(year)}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="mt-1 text-[11px] leading-snug text-gray-500">
                      The twelve months this plan covers (January–December {newVersionYear}).
                    </p>
                  </div>
                ) : null}
                {createMode === "duplicate" ? (
                  <div>
                    <Label htmlFor="duplicate-source">Copy from</Label>
                    <Select
                      value={duplicateSourceId || versionManager.current_version_id || undefined}
                      onValueChange={(v: string) => setDuplicateSourceId(v)}
                    >
                      <SelectTrigger id="duplicate-source" className="mt-1 w-full">
                        <SelectValue placeholder="Select a forecast version" />
                      </SelectTrigger>
                      <SelectContent>
                        {versionManager.versions.map((v) => (
                          <SelectItem key={v.id} value={v.id}>
                            {v.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
                <div>
                  <Label htmlFor="new-version-name">Version Name</Label>
                  <Input
                    id="new-version-name"
                    value={newVersionName}
                    onChange={(e) => setNewVersionName((e.target as HTMLInputElement).value)}
                    placeholder="Enter version name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="new-version-description">Description (Optional)</Label>
                  <Input
                    id="new-version-description"
                    value={newVersionDescription}
                    onChange={(e) => setNewVersionDescription((e.target as HTMLInputElement).value)}
                    placeholder="Enter description"
                    className="mt-1"
                  />
                </div>
                {createMode !== "duplicate" ? (
                  <div>
                    <Label htmlFor="new-version-plan-kind">Plan</Label>
                    {/* A budget is its own plan with its own primary; a copy keeps its source's kind. */}
                    <Select value={newVersionPlanKind} onValueChange={(v: string) => setNewVersionPlanKind(v)}>
                      <SelectTrigger id="new-version-plan-kind" className="mt-1 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PLAN_KIND_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="mt-1 text-[11px] leading-snug text-gray-500">
                      Forecast and budget each have their own primary version.
                    </p>
                  </div>
                ) : null}
                {createMode !== "duplicate" ? (
                  <div>
                    <Label htmlFor="new-version-scenario">Scenario</Label>
                    <Select value={newVersionScenarioKind} onValueChange={(v: string) => setNewVersionScenarioKind(v)}>
                      <SelectTrigger id="new-version-scenario" className="mt-1 w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SCENARIO_KIND_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
                <div className="flex gap-2">
                  <Button
                    onClick={handleCreateVersion}
                    disabled={
                      !newVersionName.trim() ||
                      (createMode === "duplicate" &&
                        !(duplicateSourceId || versionManager.current_version_id))
                    }
                  >
                    Create
                  </Button>
                  <Button variant="outline" onClick={handleCancel}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : isEditing ? (
              <div className="space-y-3">
                <h4 className="font-medium text-gray-900">Edit Version</h4>
                <div>
                  <Label htmlFor="edit-version-name">Version Name</Label>
                  <Input
                    id="edit-version-name"
                    value={newVersionName}
                    onChange={(e) => setNewVersionName((e.target as HTMLInputElement).value)}
                    placeholder="Enter version name"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-version-description">Description (Optional)</Label>
                  <Input
                    id="edit-version-description"
                    value={newVersionDescription}
                    onChange={(e) => setNewVersionDescription((e.target as HTMLInputElement).value)}
                    placeholder="Enter description"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-version-scenario">Scenario</Label>
                  <Select value={newVersionScenarioKind} onValueChange={(v: string) => setNewVersionScenarioKind(v)}>
                    <SelectTrigger id="edit-version-scenario" className="mt-1 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SCENARIO_KIND_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button onClick={handleUpdateVersion} disabled={!newVersionName.trim()}>
                    Update
                  </Button>
                  <Button variant="outline" onClick={handleCancel}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                onClick={() => {
                  setDuplicateSourceId(versionManager.current_version_id || "")
                  setCreateMode("blank")
                  setIsCreating(true)
                }}
                className="w-full h-11 justify-center"
              >
                <Plus className="h-4 w-4 mr-2 shrink-0" />
                New version
              </Button>
            )}

            {/* All Versions — grouped under parent scenario when `parent_version_id` is set */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold text-gray-900">All versions</h4>
              <p className="text-[11px] leading-snug text-gray-500">
                Restored and duplicated scenarios appear nested under the version they branched from.
              </p>
              <div className="space-y-3">
                {version_forest.map((node: VersionTreeNode) => (
                  <VersionTreeBranch
                    key={node.version.id}
                    node={node}
                    depth={0}
                    parent_name={null}
                    versionManager={versionManager}
                    onVersionChange={onVersionChange}
                    onClose={onClose}
                    onVersionDelete={(version: ForecastVersion) => setPendingDeleteVersion(version)}
                    onVersionDuplicate={onVersionDuplicate}
                    onVersionSetPrimary={onVersionSetPrimary}
                    onVersionToggleLocked={handleToggleLocked}
                    onVersionToggleArchived={(version: ForecastVersion) =>
                      onVersionSetArchived(version.id, !version.archived_at)
                    }
                    handleEditVersion={handleEditVersion}
                    handleDuplicateVersion={handleDuplicateVersion}
                    formatDate={formatDate}
                  />
                ))}
              </div>
              {archived_count > 0 ? (
                <button
                  type="button"
                  className="text-xs font-medium text-blue-700 hover:text-blue-900"
                  onClick={() => setShowArchived(!showArchived)}
                >
                  {showArchived ? "Hide archived" : `Show archived (${archived_count})`}
                </button>
              ) : null}
            </div>
          </div>
        </div>

      {/* One click on a trash icon used to destroy a scenario and its history outright. */}
      <AlertDialog
        open={pendingDeleteVersion != null}
        onOpenChange={(next: boolean) => {
          if (!next) {
            setPendingDeleteVersion(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this version?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-left text-sm text-gray-700">
              <span className="block">
                <strong>{pendingDeleteVersion?.name}</strong> and its entire change history will be
                permanently deleted. This cannot be undone.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel type="button">Keep version</AlertDialogCancel>
            <AlertDialogAction
              type="button"
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={() => {
                const version = pendingDeleteVersion
                setPendingDeleteVersion(null)
                if (version) {
                  onVersionDelete(version.id)
                }
              }}
            >
              Delete version
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}